import { IconMail } from '@tabler/icons-react';
import { Menu } from '@zorro/design-system/v2/Menu';

import { useAnalytics } from '../../Analytics';
import {
  actionsMenuTooltipLabels,
  calculateElectionWindowLaunchStatus,
  getCobraEligibilityStatus,
  getOnboardingPeriodsStatus,
} from '../ActionsMenuUtils';
import { ActionsMenuPrerequisitesTooltip } from '../ActionsRequirementTooltip';
import { ActionsSubMenu } from '../ActionsSubMenu';
import { ActionsMenuSection } from '../EmployeeActionsUtils';
import { EmployeeActionsOverlays } from '../employeeActions.consts';

const EmailCommunicationsSection = ({
  recordsForActions,
  setOverlayId,
  isInEnrollmentsDatatable,
}: ActionsMenuSection & { isInEnrollmentsDatatable: boolean }) => {
  const { sendEvent } = useAnalytics();

  const {
    areAllOnboardingPeriodsActive,
    areAllOnboardingPeriodsSubmitted,
    areAllOnboardingPeriodsEnrolled,
    areAllOnboardingPeriodsWaived,
  } = getOnboardingPeriodsStatus(recordsForActions);

  const isElectionWindowLaunch =
    calculateElectionWindowLaunchStatus(recordsForActions);
  const areAllCobraEligible = getCobraEligibilityStatus(recordsForActions);

  return (
    <ActionsSubMenu title="Email communication" icon={IconMail}>
      <Menu.Item
        onClick={() => {
          void sendEvent('click_action', {
            action_category: 'email_communication',
            action_name: 'EMAIL_ACCOUNT_INVITATION',
          });
          setOverlayId(EmployeeActionsOverlays.EMAIL_ACCOUNT_INVITATION);
        }}
      >
        {EmployeeActionsOverlays.EMAIL_ACCOUNT_INVITATION}
      </Menu.Item>

      <ActionsMenuPrerequisitesTooltip
        title="Election window launch"
        isAllEqual={isElectionWindowLaunch}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: isElectionWindowLaunch,
            label: actionsMenuTooltipLabels.isElectionWindowLaunch,
          },
        ]}
      >
        <Menu.Item
          disabled={!isElectionWindowLaunch}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'email_communication',
              action_name: 'EMAIL_ELECTION_WINDOW_LAUNCH',
            });
            setOverlayId(EmployeeActionsOverlays.EMAIL_ELECTION_WINDOW_LAUNCH);
          }}
        >
          {EmployeeActionsOverlays.EMAIL_ELECTION_WINDOW_LAUNCH}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      <ActionsMenuPrerequisitesTooltip
        title="Election deadline reminder"
        isAllEqual={areAllOnboardingPeriodsActive}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: areAllOnboardingPeriodsActive,
            label: actionsMenuTooltipLabels.areAllOnboardingPeriodsActive,
          },
        ]}
      >
        <Menu.Item
          disabled={!areAllOnboardingPeriodsActive}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'email_communication',
              action_name: 'EMAIL_ELECTION_DEADLINE_REMINDER',
            });
            setOverlayId(
              EmployeeActionsOverlays.EMAIL_ELECTION_DEADLINE_REMINDER
            );
          }}
        >
          {EmployeeActionsOverlays.EMAIL_ELECTION_DEADLINE_REMINDER}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      <ActionsMenuPrerequisitesTooltip
        title="Election summary"
        isAllEqual={areAllOnboardingPeriodsSubmitted}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: areAllOnboardingPeriodsSubmitted,
            label: actionsMenuTooltipLabels.areAllOnboardingPeriodsSubmitted,
          },
        ]}
      >
        <Menu.Item
          disabled={!areAllOnboardingPeriodsSubmitted}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'email_communication',
              action_name: 'EMAIL_ELECTION_SUMMARY',
            });
            setOverlayId(EmployeeActionsOverlays.EMAIL_ELECTION_SUMMARY);
          }}
        >
          {EmployeeActionsOverlays.EMAIL_ELECTION_SUMMARY}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      <ActionsMenuPrerequisitesTooltip
        title="Enrollment confirmation"
        isAllEqual={areAllOnboardingPeriodsEnrolled}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: areAllOnboardingPeriodsEnrolled,
            label: actionsMenuTooltipLabels.areAllOnboardingPeriodsEnrolled,
          },
        ]}
      >
        <Menu.Item
          disabled={!areAllOnboardingPeriodsEnrolled}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'email_communication',
              action_name: 'EMAIL_ENROLLMENT_CONFIRMATION',
            });
            setOverlayId(EmployeeActionsOverlays.EMAIL_ENROLLMENT_CONFIRMATION);
          }}
        >
          {EmployeeActionsOverlays.EMAIL_ENROLLMENT_CONFIRMATION}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      <ActionsMenuPrerequisitesTooltip
        title="Waiver confirmation"
        isAllEqual={areAllOnboardingPeriodsWaived}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: areAllOnboardingPeriodsWaived,
            label: actionsMenuTooltipLabels.areAllOnboardingPeriodsWaived,
          },
        ]}
      >
        <Menu.Item
          disabled={!areAllOnboardingPeriodsWaived}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'email_communication',
              action_name: 'EMAIL_WAIVER_CONFIRMATION',
            });
            setOverlayId(EmployeeActionsOverlays.EMAIL_WAIVER_CONFIRMATION);
          }}
        >
          {EmployeeActionsOverlays.EMAIL_WAIVER_CONFIRMATION}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      <ActionsMenuPrerequisitesTooltip
        title="Termination email (COBRA)"
        isAllEqual={areAllCobraEligible}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: areAllCobraEligible,
            label: actionsMenuTooltipLabels.areAllCobraEligible,
          },
        ]}
      >
        <Menu.Item
          disabled={!areAllCobraEligible}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'email_communication',
              action_name: 'EMAIL_COBRA_TERMINATION',
            });
            setOverlayId(EmployeeActionsOverlays.EMAIL_COBRA_TERMINATION);
          }}
        >
          {EmployeeActionsOverlays.EMAIL_COBRA_TERMINATION}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>
    </ActionsSubMenu>
  );
};

export { EmailCommunicationsSection };
